import * as THREE from 'three';

import { CAMERA } from '../game/constants';

/**
 * Front-facing camera rig.
 *
 * A perspective camera is used rather than an orthographic one: the board reads
 * as roughly flat either way, but perspective preserves the depth cues that
 * sell "the ball drops into a pocket" and the thickness of the transparent
 * guard.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT FRAMES
 * ---------------------------------------------------------------------------
 * THE WHOLE MACHINE, not the board. The design calls for the player to see all
 * three of its parts at once - the scoring row along the bottom, the maze in the
 * middle, and the display at the top - because the aim only means anything if
 * the pockets are on screen while the player is choosing one.
 *
 * The camera therefore fits a RECTANGLE (`fitHalfWidth` x `fitHalfHeight`,
 * centred on `centreY`) rather than a radius. A radius was the right shape when
 * the only thing on screen was a circular board; it is the wrong shape now,
 * because the machine is 3.2 m wide and 3.4 m tall, and a radius large enough to
 * hold the height would leave the width half empty.
 *
 * It is pushed back far enough to satisfy BOTH the vertical and the horizontal
 * constraint, so a portrait phone simply pulls back instead of cropping the
 * marquee off the top.
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;

  constructor() {
    this.camera = new THREE.PerspectiveCamera(CAMERA.fovDeg, 1, CAMERA.near, CAMERA.far);
    this.camera.position.set(0, CAMERA.centreY, 6);
    this.camera.lookAt(0, CAMERA.centreY, 0);
  }

  /** Fit the whole cabinet to the viewport. */
  resize(width: number, height: number): void {
    const aspect = width / Math.max(height, 1);
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();

    const halfFov = THREE.MathUtils.degToRad(CAMERA.fovDeg) / 2;
    const distanceVertical = CAMERA.fitHalfHeight / Math.tan(halfFov);
    const distanceHorizontal = CAMERA.fitHalfWidth / (Math.tan(halfFov) * Math.max(aspect, 0.0001));

    this.camera.position.set(0, CAMERA.centreY, Math.max(distanceVertical, distanceHorizontal));
    this.camera.lookAt(0, CAMERA.centreY, 0);
  }

  get distance(): number {
    return this.camera.position.z;
  }
}
